import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

/**
 * v3 cutover fix-up (J3) — top-right toast stack with auto-dismiss.
 * Replaces every `window.alert(…)` site in the apps. The provider mounts
 * once at the React tree root; consumers call `useToast()` to fire.
 *
 * Tones: error / success / info — wired to the v3 status-* tokens.
 *
 * Implementation note: timers are cleared on unmount so a fast user
 * navigating away doesn't trigger setState on a dead component.
 */

export type ToastTone = 'error' | 'success' | 'info';

export interface ToastEntry {
  id: number;
  message: string;
  tone: ToastTone;
}

interface ToastApi {
  showError: (message: string) => void;
  showSuccess: (message: string) => void;
  showInfo: (message: string) => void;
  dismiss: (id: number) => void;
}

const ToastCtx = createContext<ToastApi | null>(null);

const DISMISS_AFTER_MS = 5_000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());
  const seqRef = useRef(0);

  const dismiss = useCallback((id: number) => {
    const t = timers.current.get(id);
    if (t) {
      clearTimeout(t);
      timers.current.delete(id);
    }
    setToasts((prev) => prev.filter((entry) => entry.id !== id));
  }, []);

  const push = useCallback(
    (message: string, tone: ToastTone) => {
      seqRef.current += 1;
      const id = seqRef.current;
      setToasts((prev) => [...prev, { id, message, tone }]);
      const handle = setTimeout(() => dismiss(id), DISMISS_AFTER_MS);
      timers.current.set(id, handle);
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({
      showError: (msg) => push(msg, 'error'),
      showSuccess: (msg) => push(msg, 'success'),
      showInfo: (msg) => push(msg, 'info'),
      dismiss,
    }),
    [push, dismiss],
  );

  useEffect(() => {
    const map = timers.current;
    return () => {
      for (const handle of map.values()) clearTimeout(handle);
      map.clear();
    };
  }, []);

  return (
    <ToastCtx.Provider value={api}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastCtx.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastCtx);
  if (!ctx) {
    throw new Error('useToast called outside <ToastProvider>');
  }
  return ctx;
}

/**
 * Optional accessor — returns a no-op shim when no provider is mounted.
 * Useful for primitives that may be rendered in isolated test trees.
 */
export function useToastOptional(): ToastApi {
  const ctx = useContext(ToastCtx);
  if (ctx) return ctx;
  return {
    showError: () => {},
    showSuccess: () => {},
    showInfo: () => {},
    dismiss: () => {},
  };
}

function ToastViewport({
  toasts,
  onDismiss,
}: {
  toasts: ToastEntry[];
  onDismiss: (id: number) => void;
}) {
  return (
    <div className="pointer-events-none fixed right-4 top-4 z-[1000] flex w-[360px] max-w-[calc(100vw-2rem)] flex-col gap-2">
      {toasts.map((t) => (
        <ToastBubble key={t.id} entry={t} onDismiss={() => onDismiss(t.id)} />
      ))}
    </div>
  );
}

function ToastBubble({ entry, onDismiss }: { entry: ToastEntry; onDismiss: () => void }) {
  const tone =
    entry.tone === 'error'
      ? 'border-status-err/40 bg-status-err-tint text-status-err'
      : entry.tone === 'success'
        ? 'border-status-ok/40 bg-status-ok-tint text-status-ok'
        : 'border-status-info/40 bg-status-info-tint text-status-info';
  return (
    <div
      role="status"
      aria-live="polite"
      className={[
        'pointer-events-auto flex items-start gap-3 rounded-token-md border bg-paper-1 px-3 py-2 shadow-2',
        tone,
      ].join(' ')}
    >
      <div className="flex-1 text-meta leading-snug">{entry.message}</div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Luk"
        className="text-ink-2 hover:text-ink-0"
      >
        ✕
      </button>
    </div>
  );
}
